import { useEffect, useState } from "react";
import { Cookie, X } from "lucide-react";

/**
 * CookieBanner — banner "cookie tecnici" mostrato al primo accesso.
 *
 * Epic Flow utilizza SOLO cookie tecnici essenziali per il funzionamento del sito
 * (autenticazione HttpOnly `access_token`, sessione). Nessun cookie di profilazione,
 * nessun tracker di terze parti, nessun cookie pubblicitario.
 *
 * Il banner ha finalità informativa (art. 122 Codice Privacy IT + Cookie Law):
 * - Prima visita → banner visibile
 * - Bottone "Ho capito" → salva la preferenza in localStorage
 * - Link alla Cookie Policy completa
 * - Bottone X → chiude senza salvare (riappare al prossimo caricamento)
 *
 * Non blocca nulla: i cookie tecnici sono comunque leciti anche senza consenso
 * esplicito (perché essenziali al servizio richiesto dall'utente).
 */
const STORAGE_KEY = "epicflow_cookie_ack_v1";

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const ack = window.localStorage.getItem(STORAGE_KEY);
      if (!ack) {
        // Piccolo delay per non impattare LCP e mostrare il banner dopo il primo render
        const t = setTimeout(() => setVisible(true), 900);
        return () => clearTimeout(t);
      }
    } catch (_e) {
      /* localStorage non disponibile (private mode) → non mostriamo il banner */
    }
  }, []);

  const acknowledge = () => {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ ack: true, at: new Date().toISOString() })
      );
    } catch (_e) { /* noop */ }
    setVisible(false);
  };

  const dismiss = () => setVisible(false);

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      role="dialog"
      aria-label="Informativa cookie"
      className="fixed bottom-4 left-4 right-4 md:left-6 md:right-auto md:max-w-md z-[90]"
    >
      <div className="bg-foreground text-background rounded-3xl shadow-2xl p-5 md:p-6 relative border border-background/10">
        <button
          type="button"
          aria-label="Chiudi"
          onClick={dismiss}
          data-testid="cookie-banner-dismiss"
          className="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-background/10 flex items-center justify-center text-background/70 hover:text-background transition-colors"
        >
          <X className="w-4 h-4" strokeWidth={1.5} />
        </button>

        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-2xl bg-primary/25 flex items-center justify-center flex-shrink-0">
            <Cookie className="w-5 h-5 text-primary" strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0 pt-1">
            <div className="text-[10px] uppercase tracking-[0.32em] text-primary/80 mb-1">
              Navigazione sicura
            </div>
            <h3 className="font-serif-display text-lg leading-tight mb-2">
              Cookie tecnici essenziali
            </h3>
            <p className="text-sm text-background/85 leading-relaxed">
              Usiamo <strong>solo cookie tecnici</strong> per farti restare loggata,
              proteggere il tuo account e ricordare le tue preferenze. Nessun tracker
              pubblicitario, nessuna profilazione.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                data-testid="cookie-banner-accept"
                onClick={acknowledge}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-primary-foreground text-xs uppercase tracking-widest btn-lift"
              >
                Ho capito
              </button>
              <a
                data-testid="cookie-banner-policy"
                href="/legal/cookie"
                className="text-xs uppercase tracking-widest text-background/70 hover:text-background underline underline-offset-4"
              >
                Cookie policy
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
